import { RootState } from 'packages/blu-booking/src/store';
import { memo, useDeferredValue, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Scheduler } from './UI/Scheduler';
import { CalendarLoader } from '../../UI/CalendarLoader/CalendarLoader';
import { usePointOfSaleHoursCalendar } from 'packages/blu-booking/src/data_fetching/PointOfSaleHours/PointOfSaleHoursCalendar';
import { usePointOfSaleHoursShift } from 'packages/blu-booking/src/data_fetching/PointOfSaleHours/PointOfSaleHoursShift';
import { Stack } from '@mui/material';
import { selectSoloOrario } from '../../hooks/useSoloOrario';
import { useFinestraOraria } from '../../hooks/useFinestraOraria';
import { selectAppuntamentiDefaultPronti } from './utility_objects/AppuntamentiDefault';

export const CalendarDefaultInner = memo(
  function CalendarDefaultInner(): JSX.Element {
    const { filtersLoad } = useSelector(
      (state: RootState) => state.bluBookingCalendarFilters
    );

    const { selectedDate, view, selectedPointOfSale, selectedUsers } =
      useSelector((state: RootState) => state.bluBookingDefaultCalendar);
    // Riga Ore del riquadro Visualizzazione di questo calendario
    const soloOrario = useSelector(selectSoloOrario('negozio'));
    const appointments = useSelector(
      (state: RootState) => state.bluBookingaAppointments.appointments
    );
    const appuntamentiPronti = useSelector(selectAppuntamentiDefaultPronti);

    const {
      data: calendar,
      isLoading: isLoadingCalendarPointOfSale,
      isSuccess: isSuccessCalendarPointOfSale,
    } = usePointOfSaleHoursCalendar({
      selectedDate,
      view,
      pointOfSaleId: selectedPointOfSale?.id,
      selectedUsers,
      options: {
        enabled: filtersLoad ?? false,
        refetchInterval: 300000,
      },
    });

    const { data: shift, isLoading: isLoadingShiftPointOfSale } =
      usePointOfSaleHoursShift({
        selectedDate,
        view,
        pointOfSaleId: selectedPointOfSale?.id,
        selectedUsers,
        options: {
          enabled: filtersLoad ?? false,
          refetchInterval: 300000,
        },
      });

    const calendarPointOfSale = useDeferredValue(calendar);
    const shiftPointOfSale = useDeferredValue(shift);

    // Gli orari che arrivano allo Scheduler sono quelli della data e dei filtri
    // correnti: per un render dopo l'arrivo dei dati il valore differito è
    // ancora quello di prima (nessuno, dopo un caricamento), e lo Scheduler
    // appena rimontato lo riceve un render dopo
    const sfondoPronto =
      isSuccessCalendarPointOfSale && calendarPointOfSale === calendar;

    // Qui e non nello Scheduler, che si rimonta a ogni data, vista o filtro:
    // la finestra tenuta passa da un montaggio all'altro, senza tornare alle 5-24
    const finestra = useFinestraOraria({
      soloOrario,
      conPulsante: !!selectedPointOfSale,
      view,
      selectedDate,
      orari: calendarPointOfSale,
      appuntamenti: appointments,
      appuntamentiPronti,
      sfondoPronto,
    });

    const GetContent = useMemo(() => {
      if (isLoadingCalendarPointOfSale || isLoadingShiftPointOfSale)
        return (
          <Stack
            alignItems="center"
            justifyContent="center"
            direction="row"
            flex="1"
            sx={{
              height: '100%',
              width: '100%',
              backgroundColor: 'var(--base-foreground-color) ',
            }}
          />
        );
      return (
        <Scheduler
          calendarPointOfSale={calendarPointOfSale}
          shiftPointOfSale={shiftPointOfSale}
          startHour={finestra.startHour}
          endHour={finestra.endHour}
          finestraInAttesa={finestra.inAttesa}
          finestraProvvisoria={finestra.provvisoria}
        />
      );
    }, [
      isLoadingCalendarPointOfSale,
      isLoadingShiftPointOfSale,
      calendarPointOfSale,
      shiftPointOfSale,
      finestra.startHour,
      finestra.endHour,
      finestra.inAttesa,
      finestra.provvisoria,
    ]);

    return (
      <div key="Default" style={{ height: '100%' }}>
        {GetContent}
        <CalendarLoader
          open={isLoadingCalendarPointOfSale || isLoadingShiftPointOfSale}
        />
      </div>
    );
  }
);
